import React from "react";
import "./list-item-card.scss";

export interface ListItemCardProps
  extends Omit<React.HTMLAttributes<HTMLDivElement>, "children"> {
  /** Primary text displayed prominently (e.g. claim number). */
  title: string;
  /** Secondary text displayed below the title (e.g. amount). */
  subtitle?: string;
  /** Status label shown as a tag on the right (e.g. "Partially Paid"). */
  statusLabel?: string;
  /** Background color of the status tag. */
  statusBackgroundColor?: string;
  /** Text color of the status tag. */
  statusTextColor?: string;
  /** Whether the card is in its selected/active state. Adds a left accent border and white background. */
  selected?: boolean;
  /** Override the left accent border color when selected. Defaults to Tertiary Blue 4. */
  accentColor?: string;
  /** Whether to truncate the title, subtitle, and status label text with an ellipsis. */
  shouldTruncate?: boolean;
  /** Content rendered before the info block (e.g. checkbox, icon, avatar). */
  leadingContent?: React.ReactNode;
  /** Content rendered inline to the right of the title (e.g. copy button, badge). */
  titleAdornment?: React.ReactNode;
  /** Content rendered after the status tag at the right edge (e.g. action menu, chevron). */
  trailingContent?: React.ReactNode;
  /** Content rendered as a full-width row below the main card body (e.g. progress bar, timestamp). */
  footerContent?: React.ReactNode;
  /** Additional CSS class name. */
  className?: string;
}

export const ListItemCard: React.FC<ListItemCardProps> = ({
  title,
  subtitle,
  statusLabel,
  statusBackgroundColor,
  statusTextColor,
  selected = false,
  accentColor,
  shouldTruncate = false,
  leadingContent,
  titleAdornment,
  trailingContent,
  footerContent,
  className = "",
  ...rest
}) => {
  return (
    <div
      className={`list-item-card ${selected ? "list-item-card--selected" : ""} ${shouldTruncate ? "list-item-card--truncate" : ""} ${className}`.trim()}
      style={{
        ...(selected && accentColor
          ? { borderLeftColor: accentColor }
          : {}),
        ...rest.style,
      }}
      {...rest}
    >
      <div className="list-item-card__body">
        {leadingContent && (
          <div className="list-item-card__leading">{leadingContent}</div>
        )}

        <div className="list-item-card__info">
          <span className="list-item-card__title-row">
            <span
              className={`list-item-card__title ${shouldTruncate ? "list-item-card__title--truncate" : ""}`}
              title={shouldTruncate ? title : undefined}
            >
              {title}
            </span>
            {titleAdornment && (
              <span className="list-item-card__adornment">{titleAdornment}</span>
            )}
          </span>
          {subtitle && (
            <span
              className={`list-item-card__subtitle ${shouldTruncate ? "list-item-card__subtitle--truncate" : ""}`}
              title={shouldTruncate ? subtitle : undefined}
            >
              {subtitle}
            </span>
          )}
        </div>

        {statusLabel && (
          <span
            className={`list-item-card__status ${shouldTruncate ? "list-item-card__status--truncate" : ""}`}
            style={{
              ...(statusBackgroundColor
                ? { backgroundColor: statusBackgroundColor }
                : {}),
              ...(statusTextColor ? { color: statusTextColor } : {}),
            }}
            title={shouldTruncate ? statusLabel : undefined}
          >
            {statusLabel}
          </span>
        )}

        {trailingContent && (
          <div className="list-item-card__trailing">{trailingContent}</div>
        )}
      </div>

      {footerContent && (
        <div className="list-item-card__footer">{footerContent}</div>
      )}
    </div>
  );
};

export default ListItemCard;
